/**
 * POSLogin - Peace of Sleep Login Page
 *
 * Branded login experience for hotel managers.
 * Redirects to property selector or specific property after login.
 *
 * URL: pos.cleanmybed.com/login
 */

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import '../pos-theme.css';
import './pos-login.css';

function POSLogin() {
  const { supabase } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!email.trim() || !password) {
      setError('Please enter your email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data, error: signInError } = await supabase!.auth.signInWithPassword({
        email: email.trim(),
        password
      });

      if (signInError) {
        const msg = signInError.message?.toLowerCase() || '';
        if (msg.includes('failed to fetch') || msg.includes('networkerror') || msg.includes('load failed')) {
          setError('Unable to connect — please check your internet connection or try mobile data.');
        } else {
          setError('Invalid email or password');
        }
        setLoading(false);
        return;
      }

      // Check if user has POS portal access
      const { data: userData, error: userError } = await supabase!
        .from('users')
        .select('is_pos_staff, permissions')
        .eq('id', data.user.id)
        .single();

      if (userError || !userData) {
        setError('User not found');
        await supabase!.auth.signOut();
        setLoading(false);
        return;
      }

      // Verify POS access — check .level (standard) or .access (legacy)
      const posPerms = (userData.permissions as Record<string, Record<string, unknown>> | null)?.pos_portal;
      const hasPOSAccess = userData.is_pos_staff ||
        (posPerms?.level != null && posPerms.level !== 'off') ||
        posPerms?.access === true;

      if (!hasPOSAccess) {
        setError('You do not have access to the POS portal. Please contact your administrator.');
        await supabase!.auth.signOut();
        setLoading(false);
        return;
      }

      // Redirect to intended destination or property selector
      if (redirectTo) {
        navigate(redirectTo);
      } else {
        navigate('/pos');
      }
    } catch (err: unknown) {
      const msg = ((err as Error)?.message || '').toLowerCase();
      if (msg.includes('failed to fetch') || msg.includes('networkerror') || msg.includes('load failed')) {
        setError('Unable to connect — please check your internet connection or try mobile data.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setLoading(false);
    }
  }

  return (
    <div className="pos-login">
      <div className="pos-login-content">
        {/* Brand Header */}
        <div className="pos-login-header">
          <h1 className="pos-login-brand">PEACE OF SLEEP</h1>
          <p className="pos-login-tagline">The Quiet Standard Of Trust</p>
        </div>

        {/* Login Card */}
        <div className="pos-login-card">
          <h2>Welcome Back</h2>
          <p>Sign in to manage your property.</p>

          {error && (
            <div className="pos-login-error">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="pos-login-form">
            <div className="pos-login-field">
              <label className="pos-login-label">Email</label>
              <input
                type="email"
                className="pos-login-input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                autoComplete="email"
                autoFocus
              />
            </div>

            <div className="pos-login-field">
              <label className="pos-login-label">Password</label>
              <input
                type="password"
                className="pos-login-input"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              className="pos-login-btn"
              disabled={loading}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>

          <div className="pos-login-footer">
            <p>
              Don&apos;t have an account?
              <br />
              Contact your property administrator for an invitation.
            </p>
          </div>
        </div>

        {/* Powered By */}
        <div className="pos-login-powered-by">
          <span>Powered by</span>
          <span className="pos-login-powered-by-brand">CleanMyBed</span>
        </div>
      </div>
    </div>
  );
}

export default POSLogin;
